'use client';

import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/**
 * **The one labelled-toggle-row treatment, used everywhere a boolean setting
 * gets a title (and optionally a description) and a row of its own** (VALE-972).
 *
 * The switch is right-aligned at the row's far edge and vertically centred
 * against the *whole* label block — title and description together, never
 * against the title line alone. Modelled on the PMS sync panels
 * (`components/integrations/{actionstep,smokeball}/form/*-sync-section.tsx`),
 * which already carried this shape before this component existed.
 *
 * Does **not** cover inline toggles inside a filter bar or toolbar (no
 * description, no row of their own) — those stay hand-rolled by design.
 */
export function LabelledToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  className,
  labelClassName,
  descriptionClassName,
  ariaLabelledBy,
  switchTestId
}: {
  /** Must be unique on the page — it is what ties the label to the control. */
  id: string;
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  /** Merged onto the row's own container — background, border, padding. */
  className?: string;
  labelClassName?: string;
  descriptionClassName?: string;
  /**
   * Set only when the visible label can't be the switch's accessible name
   * via `htmlFor` alone (VALE-31: Chromium does not derive an accessible
   * name from a `<label for>` pointing at a `role="switch"` button). When
   * set, it becomes the label's own `id` and the switch's `aria-labelledby`.
   */
  ariaLabelledBy?: string;
  switchTestId?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-4', className)}>
      <div className="min-w-0 space-y-0.5">
        <Label
          htmlFor={id}
          id={ariaLabelledBy}
          className={cn('text-sm font-medium', labelClassName)}
        >
          {label}
        </Label>
        {description ? (
          <p
            className={cn(
              'text-muted-foreground text-sm',
              descriptionClassName
            )}
          >
            {description}
          </p>
        ) : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0"
        aria-labelledby={ariaLabelledBy}
        data-testid={switchTestId}
      />
    </div>
  );
}
